/**
 * Is anybody home on these relays?
 *
 * A join that never settles is the worst failure a lobby can show, so every strategy is asked this
 * first and is only opened if a socket comes up inside the budget. Dialling is parallel and the
 * first relay to open wins: one is all a session needs, and waiting for the slowest would make the
 * probe as slow as the worst relay in the list.
 *
 * It is a heuristic and says so. A relay that accepts a socket and then drops what it is told to
 * publish looks reachable here, which is why the caller keeps a second, longer budget for the
 * session actually settling.
 *
 * Trystero's own socket bookkeeping is deliberately not used: `getRelaySockets` is published as
 * `any`, and one loose type at this seam would spread unchecked values through the transport.
 */

/** Relay lists run to dozens. Enough dials to clear a few dead ones, not the whole list. */
const PROBE_FANOUT = 6;

/** Resolves true on the first socket that opens, false when the budget expires or all fail. */
export function probeRelays(
  urls: readonly string[],
  timeoutMs: number,
  signal: AbortSignal,
): Promise<boolean> {
  const targets = spread(urls, PROBE_FANOUT);
  if (targets.length === 0) return Promise.resolve(false);

  return new Promise<boolean>((resolve) => {
    const sockets: WebSocket[] = [];
    let pending = targets.length;
    let settled = false;

    const finish = (reachable: boolean): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      for (const socket of sockets) socket.close();
      resolve(reachable);
    };

    const onAbort = (): void => {
      finish(false);
    };
    const timer = setTimeout(() => {
      finish(false);
    }, timeoutMs);
    signal.addEventListener('abort', onAbort, { once: true });

    for (const url of targets) {
      let socket: WebSocket;
      try {
        socket = new WebSocket(url);
      } catch {
        // A malformed or blocked URL is one dead relay, not a dead strategy.
        pending -= 1;
        if (pending === 0) finish(false);
        continue;
      }

      sockets.push(socket);
      socket.addEventListener('open', () => {
        finish(true);
      });
      socket.addEventListener('error', () => {
        pending -= 1;
        if (pending === 0) finish(false);
      });
    }
  });
}

/**
 * Takes `count` entries spread across the list rather than the first few, so a probe does not
 * decide a whole strategy is down because the top of somebody's relay list went stale.
 */
function spread(urls: readonly string[], count: number): readonly string[] {
  if (urls.length <= count) return urls;

  const step = urls.length / count;
  const picked: string[] = [];
  for (let index = 0; index < count; index += 1) {
    const url = urls[Math.floor(index * step)];
    if (url !== undefined) picked.push(url);
  }
  return picked;
}
